import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, FileText, FolderOpen, BookOpen } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";

const CATS = [
  { key: "materi", label: "Materi Kepramukaan", icon: BookOpen },
  { key: "aturan", label: "Aturan Kepramukaan", icon: FolderOpen },
];

export default function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", category: "materi", description: "", file_path: "", filename: "" });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("file_path", data.path); upd("filename", data.filename);
      if (!form.title) upd("title", data.filename.replace(/\.[^.]+$/, ""));
      toast.success("Berkas terunggah");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal unggah");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_path) return toast.error("Unggah berkas terlebih dahulu");
    setSaving(true);
    try {
      await api.post("/documents", form);
      toast.success("Dokumen dipublikasikan");
      setForm({ title: "", category: "materi", description: "", file_path: "", filename: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    toast.success("Dokumen dihapus");
    load();
  };

  const inputCls = "w-full rounded-lg border border-[#E5DFD7] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#E5A93C] focus:ring-2 focus:ring-[#E5A93C]/30 transition";
  const labelCls = "block text-xs uppercase tracking-widest font-bold text-[#8C827A] mb-1.5";

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-extrabold text-[#1F1917]">Dokumen Publik</h1>
          <p className="text-[#8C827A] mt-1">Kelola materi dan aturan kepramukaan yang dapat diunduh dari halaman depan.</p>
        </div>

        <div className="grid lg:grid-cols-[380px_1fr] gap-6">
          <form onSubmit={submit} className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-6 space-y-4 self-start">
            <h2 className="font-display text-lg font-semibold text-[#1F1917]">Tambah Dokumen</h2>
            <div>
              <label className={labelCls}>Kategori</label>
              <select data-testid="doc-select-category" className={inputCls} value={form.category} onChange={(e) => upd("category", e.target.value)}>
                {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Judul</label>
              <input data-testid="doc-input-title" className={inputCls} value={form.title} onChange={(e) => upd("title", e.target.value)} required placeholder="cth: Buku Saku Pramuka Penggalang" />
            </div>
            <div>
              <label className={labelCls}>Deskripsi (opsional)</label>
              <textarea className={inputCls + " min-h-[70px]"} value={form.description} onChange={(e) => upd("description", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Berkas (PDF/DOC/PPT/XLS/Gambar, maks 20MB)</label>
              <label data-testid="doc-upload-file" className="flex items-center justify-center gap-2 border-2 border-dashed border-[#E5DFD7] rounded-lg py-6 cursor-pointer hover:border-[#E5A93C] hover:bg-[#F7F4EF] transition text-sm text-[#8C827A]">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {form.filename ? <span className="text-[#4A2E2B] font-semibold truncate max-w-[220px]">{form.filename}</span> : "Klik untuk unggah berkas"}
                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.webp" onChange={(e) => handleUpload(e.target.files[0])} />
              </label>
            </div>
            <button data-testid="doc-submit-btn" type="submit" disabled={saving || uploading}
              className="w-full bg-[#4A2E2B] text-white py-3 rounded-lg font-bold hover:bg-[#3a2320] transition disabled:opacity-60 flex items-center justify-center gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Publikasikan
            </button>
          </form>

          <div className="space-y-6">
            {CATS.map((c) => {
              const Icon = c.icon;
              const list = docs.filter((d) => d.category === c.key);
              return (
                <div key={c.key} className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-6" data-testid={`doc-list-${c.key}`}>
                  <h2 className="font-display text-lg font-semibold text-[#1F1917] flex items-center gap-2 mb-4"><Icon className="h-5 w-5 text-[#B85C38]" />{c.label} <span className="text-xs text-[#8C827A] font-normal">({list.length})</span></h2>
                  {loading ? <Loader2 className="h-5 w-5 animate-spin text-[#B85C38]" /> : list.length === 0 ? (
                    <p className="text-sm text-[#8C827A]">Belum ada dokumen.</p>
                  ) : list.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 py-3 border-t border-[#F1ECE5] first:border-t-0">
                      <FileText className="h-5 w-5 text-[#B85C38] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-[#1F1917] truncate">{d.title}</div>
                        <div className="text-xs text-[#8C827A] truncate">{d.filename}{d.description ? ` · ${d.description}` : ""}</div>
                      </div>
                      <a href={`${api.defaults.baseURL}/documents/${d.id}/download`} className="text-xs font-bold text-[#B85C38] hover:underline">Unduh</a>
                      <button data-testid="doc-delete-btn" onClick={() => remove(d.id)} className="p-2 rounded-lg text-[#D9383A] hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
